import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type {
  BelegungMonatRow,
  BelegungMonatUpsertBody,
  GruppePatch,
  GruppeRow,
  GruppeWrite,
  PersonalbedarfResponse,
  UUID,
} from './types';

// --- Gruppen ---------------------------------------------------------------

export function useGruppen(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.belegung.gruppen(planId) : ['belegung', 'gruppen', 'none'],
    queryFn: async (): Promise<GruppeRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<GruppeRow[]>(`/plan/${planId}/belegung/gruppen`);
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreateGruppe(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: GruppeWrite): Promise<GruppeRow> => {
      const res = await apiClient.post<GruppeRow>(`/plan/${planId}/belegung/gruppen`, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.gruppen(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.monate(planId) });
    },
  });
}

export function useUpdateGruppe(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID; patch: GruppePatch }): Promise<GruppeRow> => {
      const res = await apiClient.patch<GruppeRow>(
        `/plan/${planId}/belegung/gruppen/${vars.id}`,
        vars.patch,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.gruppen(planId) });
    },
  });
}

export function useDeleteGruppe(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/belegung/gruppen/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.gruppen(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.monate(planId) });
    },
  });
}

// --- Belegungs-Monate ------------------------------------------------------

export function useBelegungMonate(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.belegung.monate(planId) : ['belegung', 'monate', 'none'],
    queryFn: async (): Promise<BelegungMonatRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<BelegungMonatRow[]>(`/plan/${planId}/belegung/monate`);
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useUpsertBelegungMonat(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BelegungMonatUpsertBody): Promise<BelegungMonatRow> => {
      const res = await apiClient.put<BelegungMonatRow>(
        `/plan/${planId}/belegung/monate`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.monate(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.belegung.personalbedarfAlle(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
      // Die Landesfoerderung-Aufschluesselung rechnet live aus Belegung,
      // Pauschalen-Auswahl und Kinderliste — sie muss hier mitziehen.
      void qc.invalidateQueries({
        queryKey: queryKeys.einnahmen.landAufschluesselung(planId),
      });
    },
  });
}

/**
 * Personalbedarf je Monat.
 *
 * ``basis='betriebserlaubnis'`` rechnet dieselbe Formel auf den Plätzen
 * laut Betriebserlaubnis statt auf der Belegungsmatrix — wie viel Personal
 * bräuchte die Einrichtung bei voller Auslastung. Die beiden Sichten liegen
 * unter getrennten Query-Keys, damit ein Wechsel nicht die jeweils andere
 * aus dem Cache verdrängt.
 */
export function usePersonalbedarf(
  planId: UUID | null,
  basis: 'belegung' | 'betriebserlaubnis' = 'belegung',
) {
  return useQuery({
    queryKey: planId
      ? queryKeys.belegung.personalbedarf(planId, basis)
      : ['belegung', 'personalbedarf', 'none'],
    queryFn: async (): Promise<PersonalbedarfResponse> => {
      if (!planId) return { monatswerte: [], max_h_woche: 0 };
      const res = await apiClient.get<PersonalbedarfResponse>(
        `/plan/${planId}/belegung/personalbedarf`,
        { params: { basis } },
      );
      return res.data;
    },
    enabled: !!planId,
  });
}
